// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import { arrayOf, shape, string, object } from 'prop-types';
import { includes, replace, get } from 'lodash';

import { Breadcrumb } from '../../components';
import { getI18nFormattedMessage } from '../../utils/intl';
import { hasPublisherRole, hasAdminRole } from '../../utils';
import {
  getUserDetails,
} from '../../reducers/portalConfig';
import {
  HOME, API_LIST, API_PLANS_ADD, API_PLANS_LIST, API_DETAILS, CUSTOM_FIELD_LIST,
  APPS_LIST, APPS_DETAILS, APPEARANCE, APPEARANCE_DETAILS, GROUPS_LIST, GROUPS_DETAILS,
  GATEWAY_BUNDLES_LIST, GATEWAY_BUNDLES_DETAILS, KEY_STORES_LIST, KEY_STORES_DETAILS,
  ORGS_LIST, ORGS_DETAILS, TEMPLATE_LIST, TEMPLATE_DETAILS, PRODUCT_LIST, PRODUCT_DETAILS,
  RATE_QUOTAS_LIST, RATE_QUOTAS_DETAILS, USERS_LIST, USERS_DETAILS, APPEARANCE_GLOBAL,
  APPLICATION_AND_KEY_SETTINGS, APPLICATION_AND_KEY_SETTINGS_AUTH_PROVIDER, PROXIES_LIST,
  PROXIES_DETAILS, API_PLANS_DETAILS, ADMIN_SETTINGS_AUTH_LIST, ADMIN_SETTINGS, EULAS_LIST,
  EULAS_DETAILS, MONITOR, MY_PROFILE,
} from './constants';
import styles from './styles';

const BreadcrumbsForPages = (props) => {
  const { pathName = '', items = [], classes, pageContainerClass,
  portalConfig, userContext } = props;
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [modifyItems, setModifyItems] = useState([]);
  const [containerClass, setContainerClass] = useState(classes.pageContainer);

  useEffect(() => {
    const pathNames = pathName && pathName.split('/').filter(x => x) || [];
    if (pathNames.length === 1 && pathNames[0] === 'admin') {
      setContainerClass(classes.pageContainer);
      setBreadcrumbItems(HOME);
    } else if (pathNames[0] === 'publish') {
      //eulas
      if (pathNames[1] === 'eulas') {
        if (pathNames.length === 2) {
          setBreadcrumbItems(EULAS_LIST);
        }
        if (pathNames[2] === 'edit' || pathNames[2] === 'add') {
          setContainerClass(pageContainerClass);
          setBreadcrumbItems(items);
        }
        if (pathNames[2] === 'details') {
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems([...EULAS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.eulas.details') },
          ]]);
        }
      }
      // custom field
      if(pathNames[1] === 'settings' && pathNames[2] === 'custom-fields') {
        if(pathNames.length === 3) {
          setBreadcrumbItems(CUSTOM_FIELD_LIST);
        }
        if(includes(pathNames, 'edit') || includes(pathNames, 'add')) {
          setContainerClass(classes.pageBreadCrumbClass);
          setBreadcrumbItems(items);
        }
      }
      //apis
      if (pathNames[1] === 'apis') {
        if (pathNames.length === 2) {
          setBreadcrumbItems(API_LIST);
        }
        if (pathNames[2] === 'details') {
          setContainerClass(classes.pageContainerDetails);
          if (pathNames.length === 4 || includes(pathNames, 'overview')) {
            setBreadcrumbItems([...API_DETAILS, ...[
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.api.details') },
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
            ]]);
          } else {
            setBreadcrumbItems([...API_DETAILS, ...[
              {
                isActive: true, link: `/publish/apis/details/${pathNames[3]}`,
                linkName: getI18nFormattedMessage('label.breadcrumb.api.details'),
              },
              { isActive: false, link: '/publish/apis/details', linkName: replace(pathNames[4], '-', ' ') },
            ]]);
          }
        }
        if (pathNames[2] === 'edit' || pathNames[2] === 'add') {
          setContainerClass(pageContainerClass);
          setBreadcrumbItems(items);
        }
      }
      // proxy
      if (pathNames[1] === 'api-proxy') {
        if (pathNames[2] === 'add' || pathNames[2] === 'edit') {
          setContainerClass(pageContainerClass);
          setBreadcrumbItems(items);
        }
        if (pathNames[2] === 'details') {
          setContainerClass(classes.pageContainerDetails);
          if (pathNames.length === 4 || includes(pathNames, 'overview')) {
            setBreadcrumbItems([...PROXIES_DETAILS, ...[
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.proxies.details') },
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
            ]]);
          } else {
            setBreadcrumbItems([...PROXIES_DETAILS, ...[
              {
                isActive: true, link: `/publish/api-proxy/details/${pathNames[3]}`,
                linkName: getI18nFormattedMessage('label.breadcrumb.proxies.details'),
              },
              {
                isActive: false, link: '',
                linkName: replace(pathNames[4], '-', ' '),
              },
            ]]);
          }
        }
      }
      //gateway bundles
      if (pathNames[1] === 'gateway-bundles') {
        if (pathNames.length === 2) {
          setBreadcrumbItems(GATEWAY_BUNDLES_LIST);
        }
        if (pathNames[2] === 'add') {
          setContainerClass(pageContainerClass);
          setBreadcrumbItems(items);
        }
        if (pathNames[2] === 'details') {
          setContainerClass(classes.pageContainer);
          if (pathNames.length === 4 || includes(pathNames, 'overview')) {
            setBreadcrumbItems([...GATEWAY_BUNDLES_DETAILS, ...[
              {
                isActive: false,
                link: '',
                linkName: getI18nFormattedMessage('label.breadcrumb.gateway.bundles.details'),
              },
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
            ]]);
          } else {
            setBreadcrumbItems([...GATEWAY_BUNDLES_DETAILS, ...[
              {
                isActive: true, link: `/publish/gateway-bundles/details/${pathNames[3]}`,
                linkName: getI18nFormattedMessage('label.breadcrumb.gateway.bundles.details'),
              },
              {
                isActive: false, link: '',
                linkName: replace(pathNames[4], '-', ' '),
              },
            ]]);
          }
        }
      }
      if (pathNames[1] === 'settings') {
        //authentication settings
        if (pathNames[2] === 'authentication') {
          if (pathNames.length === 3) {
            setContainerClass(classes.pageContainer);
            setBreadcrumbItems(ADMIN_SETTINGS_AUTH_LIST);
          }
          if (pathNames[3] === 'edit' || pathNames[3] === 'add') {
            setContainerClass(pageContainerClass);
            setBreadcrumbItems(items);
          }
        }
      }
    } else if (pathNames[0] === 'admin') {
      if (pathNames[1] === 'user-profile') {
        //user profile
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems(MY_PROFILE);
      }
      if (pathNames[1] === 'app' && pathNames[2] === 'dashboard') {
        //dashboard
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems(HOME);
      }
      if (pathNames[1] === 'settings') {
        //portal setup
        if (pathNames.length === 2) {
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.portal.setup') },
          ]]);
        }
        //api-settings
        if (pathNames[2] === 'api-settings') {
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
          ]]);
        }
        //user-settings
        if (pathNames[2] === 'user-settings') {
          setContainerClass(classes.pageContainer);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
          ]]);
        }
        //email-settings
        if (pathNames[2] === 'email-settings') {
          setContainerClass(classes.pageContainer);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
          ]]);
        }
        // application-settings
        if (pathNames[2] === 'application-settings') {
          if (pathNames.length === 3 || pathNames[3] === "clientid-secret") {
            setContainerClass(classes.pageContainerDetails);
            setBreadcrumbItems([...APPLICATION_AND_KEY_SETTINGS,
              {
                isActive: false,
                link: '',
                linkName: getI18nFormattedMessage("label.breadcrumb.application_and_key_settings.client_id_and_secret"),
              }])
          } else {
            if (pathNames[3] === "auth-providers") {
              if (pathNames.length === 4) {
                setContainerClass(classes.pageContainerDetails);
                setBreadcrumbItems([...APPLICATION_AND_KEY_SETTINGS,
                  {
                    isActive: false,
                    link: '',
                    linkName: getI18nFormattedMessage("label.breadcrumb.application_and_key_settings.auth_providers"),
                  }])
              } else if (pathNames[4] === 'add') {
                setContainerClass(pageContainerClass)
                setBreadcrumbItems([...APPLICATION_AND_KEY_SETTINGS_AUTH_PROVIDER,
                  { isActive: false, link: '', linkName: getI18nFormattedMessage("label.breadcrumb.add") }])
              } else if(pathNames.length === 7 && pathNames[5] === 'edit') {
                setContainerClass(pageContainerClass)
                setBreadcrumbItems([...APPLICATION_AND_KEY_SETTINGS_AUTH_PROVIDER,
                  { isActive: false, link: '', linkName: getI18nFormattedMessage("label.breadcrumb.edit") }])
              }
            } else if (pathNames[3] === "expiry-reporting") {
              setContainerClass(classes.pageContainerDetails);
              setBreadcrumbItems([...APPLICATION_AND_KEY_SETTINGS,
                {
                  isActive: false,
                  link: '',
                  linkName: getI18nFormattedMessage("label.breadcrumb.application_and_key_settings.expiry_reporting"),
                }])
            }
          }
        }
        //custom-pages
        if (pathNames[2] === 'custom-pages') {
          setContainerClass(classes.pageBreadCrumbClass2);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
          ]]);
        }
      }
      //audit logs
      if (pathNames[1] === 'console' && pathNames[2] === 'audit-logs') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...ADMIN_SETTINGS, ...[
          { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
        ]]);
      }
      //request-settings
      if (pathNames[1] === 'console' && pathNames[2] === 'request-settings') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...ADMIN_SETTINGS, ...[
          { isActive: false, link: '', linkName: replace(pathNames[2], '-', ' ') },
        ]]);
      }
      //requests list
      if (pathNames[1] === 'console' && pathNames[2] === 'requests') {
        // app request list
        if (pathNames.length === 3) {
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems([...ADMIN_SETTINGS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.application.requests') },
          ]]);
        }
        // user request list
        if (pathNames[3] === 'user') {
          if (pathNames.length === 4) {
            setContainerClass(classes.pageContainerDetails);
            setBreadcrumbItems([...ADMIN_SETTINGS, ...[
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.user.requests') },
            ]]);
          }
        }
        // request detail
        if (pathNames[3] === 'details') {
          setContainerClass(classes.pageContainerDetails);
          if (pathNames[4] === 'user') {
            setBreadcrumbItems([...ADMIN_SETTINGS, ...[
              {
                isActive: true, link: '/admin/console/requests',
                linkName: getI18nFormattedMessage('label.breadcrumb.requests'),
              },
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.user.request') },
            ]]);
          } else {
            setBreadcrumbItems([...ADMIN_SETTINGS, ...[
              {
                isActive: true, link: '/admin/console/requests',
                linkName: getI18nFormattedMessage('label.breadcrumb.requests'),
              },
              { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.application.request') },
            ]]);
          }
        }
      }
    }
    //api-plans
    if (pathNames[1] === 'apis' && pathNames[2] === 'api-plans') {
      if (pathNames.length === 4 && pathNames[3] === 'add') {
        setContainerClass(classes.pageContainerDetails);
        setBreadcrumbItems(API_PLANS_ADD);
      }
      if (pathNames.length === 3 && (pathNames[2] === 'api-plans')) {
        setBreadcrumbItems(API_PLANS_LIST);
      }
      if (pathNames.length === 4 && (pathNames[2] === 'enable')) {
        setBreadcrumbItems(API_PLANS_LIST);
      }
      if (pathNames[3] === 'details') {
        setContainerClass(classes.pageContainerDetails);
        if (pathNames.length === 5 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...API_PLANS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.api.plans.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...API_PLANS_DETAILS, ...[
            {
              isActive: true, link: `/admin/apis/api-plans/details/${pathNames[4]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.api.plans.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[5], '-', ' '),
            },
          ]]);
        }
      }
    }
    // proxy
    if (pathNames[1] === 'api-proxy') {
      if (pathNames.length === 2) {
        setBreadcrumbItems(PROXIES_LIST);
      }
    }
    //users
    if (pathNames[1] === 'console' && pathNames[2] === 'users') {
      if (pathNames.length === 3) {
        setBreadcrumbItems(USERS_LIST);
      }
      if (pathNames[3] === 'add') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...USERS_DETAILS, ...[
          { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
        ]]);
      }
      if (pathNames[3] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 5 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...USERS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.users.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.details') },
          ]]);
        } else {
          setBreadcrumbItems([...USERS_DETAILS, ...[
            {
              isActive: true, link: `/admin/console/users/details/${pathNames[4]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.users.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[5], '-', ' '),
            },
          ]]);
        }
      }
    }
    //rate-quotas
    if (pathNames[1] === 'rate-quotas') {
      setContainerClass(classes.pageContainer);
      if (pathNames.length === 2) {
        setBreadcrumbItems(RATE_QUOTAS_LIST);
      }
      if (pathNames[2] === 'add' || pathNames[2] === 'edit') {
        setContainerClass(pageContainerClass);
        setBreadcrumbItems(items);
      }
      if (pathNames[2] === 'details') {
        if (pathNames.length === 3) {
          setBreadcrumbItems([...RATE_QUOTAS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
          ]]);
        } else if (pathNames.length === 4 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...RATE_QUOTAS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.rate.quotas.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...RATE_QUOTAS_DETAILS, ...[
            {
              isActive: true, link: `/admin/rate-quotas/details/${pathNames[3]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.rate.quotas.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[4], '-', ' '),
            },
          ]]);
        }
      }
    }
    //products
    if (pathNames[1] === 'products') {
      setContainerClass(classes.pageContainer);
      if (pathNames.length === 2) {
        setBreadcrumbItems(PRODUCT_LIST);
      }
      if (pathNames[2] === 'tiers' && pathNames.length === 3) {
        setBreadcrumbItems([...PRODUCT_DETAILS, ...[
          { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.tiers') },
        ]]);
      }
      if (pathNames[2] === 'details') {
        if (pathNames.length === 3) {
          setBreadcrumbItems([...PRODUCT_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
          ]]);
        } else if (pathNames.length === 4 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...PRODUCT_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.products.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...PRODUCT_DETAILS, ...[
            {
              isActive: true, link: `/admin/products/details/${pathNames[3]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.products.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[4], '-', ' '),
            },
          ]]);
        }
      }
    }
    //template categories
    if (pathNames[1] === 'console' && pathNames[2] === 'templatecategories') {
      if (pathNames.length === 3) {
        setBreadcrumbItems(TEMPLATE_LIST);
      }
      if (pathNames[3] === 'add') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...TEMPLATE_DETAILS, ...[
          { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
        ]]);
      }
      if (pathNames[3] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 5 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...TEMPLATE_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.policy.templates.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...TEMPLATE_DETAILS, ...[
            {
              isActive: true, link: `/admin/console/templatecategories/details/${pathNames[4]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.policy.templates.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[5], '-', ' '),
            },
          ]]);
        }
      }
    }
    //organizations
    if (pathNames[1] === 'console' && pathNames[2] === 'organizations') {
      if (pathNames.length === 3) {
        setBreadcrumbItems(ORGS_LIST);
      }
      if (pathNames[3] === 'add') {
        setContainerClass(pageContainerClass);
        setBreadcrumbItems(items);
      }
      if (pathNames[3] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 5 || includes(pathNames, 'overview')) {
          const userDetails = get(portalConfig, 'userContexts.userContexts[0].userDetails') || userContext;
          if(!hasAdminRole(userDetails) && hasPublisherRole(userDetails)) {
            setBreadcrumbItems([...ORGS_DETAILS, ...[
              { isActive: false, link: '',
                linkName: getI18nFormattedMessage('label.breadcrumb.organizations.details') },
              { isActive: false, link: '',
                linkName: getI18nFormattedMessage('label.breadcrumb.apis') },
            ]]);
          } else {
            setBreadcrumbItems([...ORGS_DETAILS, ...[
              { isActive: false, link: '',
                linkName: getI18nFormattedMessage('label.breadcrumb.organizations.details') },
              { isActive: false, link: '',
                linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
            ]]);
          }
        } else {
          setBreadcrumbItems([...ORGS_DETAILS, ...[
            {
              isActive: true, link: `/admin/console/organizations/details/${pathNames[4]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.organizations.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[5], '-', ' '),
            },
          ]]);
        }
      }
    }
    //applications
    if (pathNames[1] === 'console' && pathNames[2] === 'applications') {
      if (pathNames.length === 3) {
        setBreadcrumbItems(APPS_LIST);
      }
      if (pathNames[3] === 'add') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...APPS_DETAILS, ...[
          { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
        ]]);
      }
      if (pathNames[3] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 5 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...APPS_DETAILS, ...[
            { isActive: false, link: '', linkName: 'application details' },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...APPS_DETAILS, ...[
            {
              isActive: true, link: `/admin/console/applications/details/${pathNames[4]}`,
              linkName: 'application details',
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[5], '-', ' '),
            },
          ]]);
        }
      }
    }
    //api Groups
    if (pathNames[1] === 'api-groups') {
      if (pathNames.length === 2) {
        setBreadcrumbItems(GROUPS_LIST);
      }
      if (pathNames[2] === 'add') {
        setContainerClass(classes.pageContainer);
        setBreadcrumbItems([...GROUPS_DETAILS, ...[
          { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
        ]]);
      }
      if (pathNames[2] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 4 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...GROUPS_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.api.groups.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...GROUPS_DETAILS, ...[
            {
              isActive: true, link: `/admin/api-groups/details/${pathNames[3]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.api.groups.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[4], '-', ' '),
            },
          ]]);
        }
      }
    }
    //key stores
    if (pathNames[1] === 'key-stores') {
      if (pathNames.length === 2) {
        setBreadcrumbItems(KEY_STORES_LIST);
      }
      if (pathNames[2] === 'details') {
        setContainerClass(classes.pageContainer);
        if (pathNames.length === 3) {
          setBreadcrumbItems([...KEY_STORES_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.add') },
          ]]);
        } else if (pathNames.length === 4 || includes(pathNames, 'overview')) {
          setBreadcrumbItems([...KEY_STORES_DETAILS, ...[
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.key.stores.details') },
            { isActive: false, link: '', linkName: getI18nFormattedMessage('label.breadcrumb.overview') },
          ]]);
        } else {
          setBreadcrumbItems([...KEY_STORES_DETAILS, ...[
            {
              isActive: true, link: `/admin/key-stores/details/${pathNames[3]}`,
              linkName: getI18nFormattedMessage('label.breadcrumb.key.stores.details'),
            },
            {
              isActive: false, link: '',
              linkName: replace(pathNames[4], '-', ' '),
            },
          ]]);
        }
      }
    }
    // analytics
    if (pathNames[1] === 'app' && pathNames[2] === 'analytics-dashboard') {
      setContainerClass(classes.pageContainer);
      if (pathNames.length === 3) {
        setBreadcrumbItems([...MONITOR,
          { isActive: false, link: '', linkName: getI18nFormattedMessage('traffic latency & errors report') }])
      } else if (pathNames[3] === 'custom-report') {
        setBreadcrumbItems([...MONITOR,
          { isActive: false, link: '', linkName: getI18nFormattedMessage('custom report') }])
      } else if (pathNames[3] === 'usage') {
        setBreadcrumbItems([...MONITOR,
          { isActive: false, link: '', linkName: getI18nFormattedMessage('quota consumption report') }])
      }
    }
    if(pathNames[0] === "appearance"){
      if(pathNames.length > 1){
        if(pathNames[1] === "organization"){
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems(APPEARANCE_DETAILS);
        }else if(pathNames[1] === "global"){
          setContainerClass(classes.pageContainerDetails);
          setBreadcrumbItems(APPEARANCE_GLOBAL)
        }
      }else{
        setBreadcrumbItems(APPEARANCE);
      }
    }
  }, [pathName, items, portalConfig, userContext]);

  useEffect(() => {
    const userDetails = get(portalConfig, 'userContexts.userContexts[0].userDetails')
    || userContext;
    const isPublishUser = hasPublisherRole(userDetails);
    const list = breadcrumbItems;
    if(list && list[0] && list[0].link && !isPublishUser) {
      list[0].link = '/admin/app/dashboard'
    }
    setModifyItems(list);
  }, [breadcrumbItems, portalConfig, userContext]);

  return (
    <Breadcrumb id={'breadcrumb-container'}
      containerClass={containerClass} breadcrubItems={modifyItems} />
  );
}

BreadcrumbsForPages.propTypes = {
  id: string,
  breadcrubItems: arrayOf(object),
  items: arrayOf(object),
  pathName: string,
  pageContainerClass: shape({}),
  classes: shape({}),
  portalConfig: shape({}),
  userContext: shape({}),
};

const mapStateToProps = state => ({
  userContext: getUserDetails(state),
});
const mapDispatchToProps = {};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(BreadcrumbsForPages);
